import React, { useState, useEffect, useContext, useRef } from 'react';
import { Box, Grid, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardCvcElement,
  CardExpiryElement,
} from '@stripe/react-stripe-js';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { removeRegister } from '../../../../app/features/appSlice';
import { loginSuccess } from '../../../../app/features/user/userSlice';
import { GridAction, CardInputWrapper, BtnSend, ButtonCheckout } from '../payment.style';
import QuestionnaireService from '../../../../core/services/questionnaire.service';
import { theme } from '../../../../theme';
import AppHelper from '../../../../core/helpers/AppHelper';
import { DialogComponent } from '../../../../core/layouts/widgets/header/header.styles';
import ascapLogo from '../../../../assets/images/ascap-logo.svg';
import { PaymentContext } from '../../summary/summary.page';
import AccountService from '../../../../core/services/account.service';
import { getBusiness, setCurrent } from '../../../../app/features/business/businessSlice';
import { setupAchPayment } from '../achPayment';

const inputStyle = {
  iconColor: '#c4f0ff',
  color: theme.palette.primary.main,
  fontWeight: '500',
  fontFamily: 'Roboto, Open Sans, Segoe UI, sans-serif',
  fontSize: '22px',
  fontSmoothing: 'antialiased',
  ':-webkit-autofill': {
    color: 'rgba(0, 0, 0, 0.87)',
  },
  '::placeholder': {
    color: 'rgba(0, 0, 0, 0.87)',
  },
};

const boxModalStyle = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '100px 72px',
  position: 'relative',
  zIndex: '2',

  '& img': {
    display: 'block',
    maxWidth: '100%',
    height: '48px',
    filter:
      'invert(100%) sepia(100%) saturate(0%) hue-rotate(349deg) brightness(101%) contrast(103%)',
  },
};

const questionnaireService = new QuestionnaireService();
const accountService = new AccountService();

const CheckoutWidget = (props) => {
  const dispatch = new useDispatch();
  const { app } = useSelector((state) => state.app);
  const { user } = useSelector((state) => state.user);
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const { isProcessing, setProcessingTo } = useContext(PaymentContext);
  const { setCheckoutError } = props;
  const [cardError, setCardError] = useState(null);
  const [isOpenDocusignModal, isSetOpenDocusingModal] = useState(false);
  const [submit, setSubmit] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const signupResultRef = useRef(null);
  const isSignupComplete = signupResultRef.current !== null;

  const { businessProviders: providers } = app.register || [];

  useEffect(() => {
    if (submit) {
      dispatch(removeRegister());
      return navigate('/account');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submit]);

  const _subscribe = async (data) => {
    let userRegistered = JSON.parse(JSON.stringify(app.register));
    userRegistered.paymentMethod = data.payment_method?.id || data.payment_method;
    userRegistered.paymentType = props.paymentType;

    try {
      const dataRegistered = Object.preventExtensions(userRegistered);
      const r = await questionnaireService.subscribe(dataRegistered);
      if (r) {
        signupResultRef.current = r.data;
        setPaymentStatus(r.data.paymentStatus);
        dispatch(loginSuccess(r.data.user));
      }
    } catch (e) {
      setCheckoutError(AppHelper.getError(e));
      await props.reloadClienteSecret();
      throw e;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isProcessing || (!signupResultRef.current && (!stripe || !elements))) {
      return;
    }
    setProcessingTo(true);
    setCheckoutError(null);
    setCardError(null);

    try {
      if (signupResultRef.current) {
        await getUserBusiness(signupResultRef.current.user);
        return;
      }

      let result;
      if (props.paymentType === 'us_bank_account') {
        const setupIntent = await setupAchPayment(stripe, props.clientSecret, {
          name: app.register.fullName,
          email: app.register.email,
        });
        result = { setupIntent };
      } else {
        const cardElement = elements.getElement(CardNumberElement);
        result = await stripe.confirmCardSetup(props.clientSecret, {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: app.register.fullName,
              email: app.register.email,
            },
          },
        });
      }

      if (result.error) {
        setCheckoutError(result.error.message);
        return;
      }

      await _subscribe(result.setupIntent);
      if (signupResultRef.current) {
        await getUserBusiness(signupResultRef.current.user);
      }
    } catch (error) {
      setCheckoutError(AppHelper.getError(error));
    } finally {
      setProcessingTo(false);
    }
  };

  const handleCardDetailsChange = (event) => {
    setCardError(event.error ? event.error.message : null);
  };

  const getUserBusiness = async (currentUser) => {
    try {
      accountService.setAccessToken(currentUser.token);
      const businessData = await accountService.getBusiness();
      if (businessData.status === 200 || businessData.status === 201) {
        const data = businessData.data;
        dispatch(getBusiness(data));
        dispatch(setCurrent(data[0]));
      }
      if (signupResultRef.current?.paymentStatus === 'processing') {
        isSetOpenDocusingModal(true);
        return;
      }
      if (!providers.includes('1')) {
        dispatch(removeRegister());
        navigate('/account');
      } else isSetOpenDocusingModal(true);
    } catch (e) {
      setCheckoutError(AppHelper.getError(e));
    }
  };

  useEffect(() => {
    if (user && !signupResultRef.current) {
      const loadBusiness = async () => {
        setProcessingTo(true);
        try {
          await getUserBusiness(user);
        } finally {
          setProcessingTo(false);
        }
      };
      loadBusiness();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <>
      <form onSubmit={handleSubmit}>
        <ToggleButtonGroup
          exclusive
          fullWidth
          disabled={isProcessing || isSignupComplete}
          value={props.paymentType}
          onChange={(event, value) => !isProcessing && !isSignupComplete && value && props.setPaymentType(value)}
          sx={{ marginBottom: 3 }}
        >
          <ToggleButton value="card">Card</ToggleButton>
          <ToggleButton value="us_bank_account">Bank account</ToggleButton>
        </ToggleButtonGroup>
        {props.paymentType === 'card' && (
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography>Card number</Typography>
              <CardInputWrapper>
                <CardNumberElement
                  options={{ style: { base: inputStyle } }}
                  onChange={handleCardDetailsChange}
                />
              </CardInputWrapper>
            </Grid>
            <Grid
              item
              xs={6}
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                flexDirection: 'column',
              }}
            >
              <Typography>Expiration date</Typography>
              <CardInputWrapper>
                <CardExpiryElement
                  options={{ style: { base: inputStyle } }}
                  onChange={handleCardDetailsChange}
                />
              </CardInputWrapper>
            </Grid>
            <Grid
              item
              xs={6}
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                flexDirection: 'column',
              }}
            >
              <Typography>CVC</Typography>
              <CardInputWrapper>
                <CardCvcElement
                  options={{ style: { base: inputStyle } }}
                  onChange={handleCardDetailsChange}
                />
              </CardInputWrapper>
            </Grid>
          </Grid>
        )}
        {props.paymentType === 'us_bank_account' && (
          <Box>
            <Typography marginBottom={2}>
              Securely connect a US bank account with Stripe.
            </Typography>
            <Typography variant="body2">
              By clicking Agree and pay, you authorize FITRADIO to debit the bank account you select
              for the amount shown and for future recurring monthly charges under your agreement
              until this authorization is revoked. You can cancel this authorization by contacting
              FITRADIO.
            </Typography>
          </Box>
        )}
        {cardError && (
          <Typography color="error" role="alert">{cardError}</Typography>
        )}
        <GridAction>
          <BtnSend type="submit" disabled={isProcessing || (!isSignupComplete && !stripe)}>
            {isProcessing
              ? 'Processing...'
              : isSignupComplete
                ? 'Continue'
                : props.paymentType === 'us_bank_account'
                  ? 'Agree and pay'
                  : 'Pay'}
          </BtnSend>
        </GridAction>
      </form>

      <DialogComponent open={isOpenDocusignModal}>
        <Box sx={boxModalStyle}>
          {paymentStatus !== 'processing' && <img src={ascapLogo} alt="" />}

          <Typography marginTop={4} variant="h1" component="h1" textAlign="center">
            {paymentStatus === 'processing'
              ? 'Your bank payment is processing'
              : 'You have a pending document to sign'}
          </Typography>
          <Typography marginTop={2}>
            {paymentStatus === 'processing'
              ? 'Your account will remain inactive until Stripe confirms the payment. We will email your licensing documents after confirmation.'
              : 'ASCAP licensing requires some additional documentation to finish registering. You can complete this through the docusign link in your email.'}
          </Typography>
          {paymentStatus !== 'processing' && (
            <Typography marginTop={2}>Check Your Email</Typography>
          )}
        </Box>
        <ButtonCheckout
          sx={{
            width: '80%',
            alignSelf: 'center',
            marginBottom: '64px',
          }}
          onClick={() => {
            setSubmit(true);
            isSetOpenDocusingModal(false);
          }}
        >
          Ok
        </ButtonCheckout>
      </DialogComponent>
    </>
  );
};

export default CheckoutWidget;

CheckoutWidget.propTypes = {
  clientSecret: PropTypes.string,
  paymentType: PropTypes.oneOf(['card', 'us_bank_account']).isRequired,
  setPaymentType: PropTypes.func.isRequired,
  reloadClienteSecret: PropTypes.func,
  setCheckoutError: PropTypes.func.isRequired,
};
